import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { dashboardApi, RobotRunStatus } from '../services/dashboardApi';
import type { BackupStatus } from '../types';
import type { UnifiedSyncStatus } from '../services/api';

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU');
  } catch {
    return iso;
  }
}

const DashboardHome: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [backup, setBackup] = useState<BackupStatus | null>(null);
  const [sync, setSync] = useState<UnifiedSyncStatus | null>(null);
  const [robots, setRobots] = useState<RobotRunStatus[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s, r] = await Promise.all([
        dashboardApi.backupSummary(),
        dashboardApi.syncSummary(),
        dashboardApi.robotsSummary(),
      ]);
      setBackup(b);
      setSync(s);
      setRobots(r);
    } catch {
      /* частичные ошибки допустимы */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const runningRobots = robots.filter((r) => r.isRunning);
  const lastSync = sync?.last_result;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Дашборд
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Резервные копии, синхронизация с 1С и фоновые роботы генерации
          </Typography>
        </Box>
        <Button startIcon={<RefreshOutlinedIcon />} onClick={load} disabled={loading} variant="outlined" size="small">
          Обновить
        </Button>
      </Stack>

      {loading && !backup && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <BackupOutlinedIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Резервная копия
                </Typography>
              </Stack>
              {backup?.last_success ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Последний успешный архив
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5 }}>
                    {fmtDate(backup.last_success.finished_at || backup.last_success.started_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {backup.last_success.file || '—'}
                    {backup.last_success.size ? ` · ${backup.last_success.size}` : ''}
                  </Typography>
                </>
              ) : (
                <Typography color="text.secondary">Успешных архивов пока нет</Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Архивов: ${backup?.archives_count ?? '—'}`} />
                <Chip size="small" label={`Объём: ${backup?.archives_total_size ?? '—'}`} variant="outlined" />
              </Stack>
              {backup?.last && !backup.last.success && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  Последняя попытка: {backup.last.error || 'ошибка'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <SyncOutlinedIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Синхронизация 1С
                </Typography>
                {sync?.in_progress && <CircularProgress size={18} />}
              </Stack>
              {sync?.in_progress ? (
                <Chip color="warning" label="Синхронизация выполняется…" size="small" sx={{ mb: 1 }} />
              ) : null}
              <Typography variant="body2" color="text.secondary">
                Последний полный прогон
              </Typography>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5 }}>
                {fmtDate(lastSync?.timestamp)}
              </Typography>
              {lastSync && (
                <Chip
                  size="small"
                  sx={{ mt: 1.5 }}
                  color={lastSync.status === 'success' ? 'success' : lastSync.status === 'partial_success' ? 'warning' : 'error'}
                  label={lastSync.status}
                />
              )}
              {lastSync?.errors?.length ? (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  {lastSync.errors[0]}
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <SmartToyOutlinedIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Роботы генерации
                </Typography>
              </Stack>
              {runningRobots.length === 0 ? (
                <Chip label="Сейчас ничего не генерируется" size="small" variant="outlined" />
              ) : (
                <Stack spacing={1}>
                  {runningRobots.map((r) => (
                    <Box key={r.key}>
                      <Typography variant="body2" fontWeight={600}>
                        {r.label}
                      </Typography>
                      {r.detail && (
                        <Typography variant="caption" color="text.secondary">
                          {r.detail}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
              <DividerList robots={robots} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const DividerList: React.FC<{ robots: RobotRunStatus[] }> = ({ robots }) => (
  <Stack spacing={0.75} sx={{ mt: 2 }}>
    {robots.map((r) => (
      <Stack key={r.key} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {r.label}
        </Typography>
        <Chip
          size="small"
          label={r.isRunning ? 'работает' : 'idle'}
          color={r.isRunning ? 'success' : 'default'}
          variant={r.isRunning ? 'filled' : 'outlined'}
        />
      </Stack>
    ))}
  </Stack>
);

export default DashboardHome;
